// ./src/components/features/Connectors/ConfigEditors/MailchimpConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
  Group,
  Box,
  List,
  Switch,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconMail,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { MailchimpConnectorConfig } from "@/types";

export interface MailchimpConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: MailchimpConnectorConfig = {
  apiKey: "",
  audienceId: "",
  existingSubscriberAction: "update",
  status: "subscribed",
  autoMapFields: true,
};

/**
 * Parse and validate Mailchimp connector configuration
 */
const parseMailchimpConfig = (jsonString: string): MailchimpConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const MailchimpConnectorConfigEditor: React.FC<
  MailchimpConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<MailchimpConnectorConfig>(() =>
    parseMailchimpConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseMailchimpConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: MailchimpConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, apiKey: e.target.value });
  };

  const handleAudienceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, audienceId: e.target.value });
  };

  const handleExistingActionChange = (value: string | null) => {
    updateConfig({
      ...config,
      existingSubscriberAction: (value as "update" | "skip") || "update",
    });
  };

  const handleStatusChange = (value: string | null) => {
    updateConfig({
      ...config,
      status: (value as "subscribed" | "pending") || "subscribed",
    });
  };

  const handleAutoMapFieldsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, autoMapFields: e.currentTarget.checked });
  };

  // Extract data center from API key
  const dataCenter = config.apiKey?.includes("-")
    ? config.apiKey.split("-").pop()
    : null;

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconMail size={16} />} color="yellow" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://admin.mailchimp.com/account/api/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mailchimp → Account → API Keys
            </Anchor>
          </List.Item>
          <List.Item>Click "Create A Key"</List.Item>
          <List.Item>Copy the API key (ends with -usXX)</List.Item>
        </List>
      </Alert>

      {/* API Key Field */}
      <TextInput
        label="API Key"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us21"
        description="Ends with data center (e.g., -us21)"
        value={config.apiKey}
        onChange={handleApiKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Show detected data center */}
      {dataCenter && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            Data center detected: <Code>{dataCenter}</Code>
          </Text>
        </Alert>
      )}

      {/* Audience ID Field */}
      <TextInput
        label="Audience ID"
        placeholder="a1b2c3d4e5"
        description="Found in Audience → Settings → Audience name and defaults"
        value={config.audienceId}
        onChange={handleAudienceIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for Audience ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Where to find Audience ID:
        </Text>
        <List size="xs" spacing={2}>
          <List.Item>
            Go to <strong>Audience</strong> → <strong>All contacts</strong>
          </List.Item>
          <List.Item>
            Click <strong>Settings</strong> dropdown
          </List.Item>
          <List.Item>
            Select <strong>Audience name and defaults</strong>
          </List.Item>
          <List.Item>
            Find <strong>Audience ID</strong> on the right
          </List.Item>
        </List>
      </Alert>

      {/* Subscriber Status */}
      <Select
        label="New Subscriber Status"
        description="How new subscribers are added"
        value={config.status || "subscribed"}
        onChange={handleStatusChange}
        disabled={disabled}
        data={[
          {
            value: "subscribed",
            label: "Subscribed (immediate, requires consent)",
          },
          { value: "pending", label: "Pending (sends confirmation email)" },
        ]}
      />

      {/* Existing Subscriber Action */}
      <Select
        label="Existing Subscribers"
        description="What to do if email already exists"
        value={config.existingSubscriberAction || "update"}
        onChange={handleExistingActionChange}
        disabled={disabled}
        data={[
          { value: "update", label: "Update their information" },
          { value: "skip", label: "Skip (do nothing)" },
        ]}
      />

      {/* Auto Map Fields Toggle */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Auto-Map Fields</Text>
            <Text size="xs" c="dimmed">
              Automatically map form fields to FNAME, LNAME, PHONE, etc.
            </Text>
          </div>
          <Switch
            checked={config.autoMapFields !== false}
            onChange={handleAutoMapFieldsChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Requirements */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="orange"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Requirements:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Form <strong>must have an email field</strong>
          </List.Item>
          <List.Item>
            For "Subscribed" status, ensure you have user consent (GDPR)
          </List.Item>
        </List>
      </Alert>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Email field is used as the subscriber email</List.Item>
          <List.Item>
            Form fields are auto-mapped to merge fields: FNAME, LNAME, PHONE,
            COMPANY, ADDRESS, BIRTHDAY
          </List.Item>
          <List.Item>Hidden and security fields are excluded</List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
